import type { Options } from "@contentful/rich-text-react-renderer";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import type { IFundamenteiBlogPostFieldsFragment } from "components/content/graphql/fragments/__generated__/FundamenteiBlogPostFields.generated";
import getYouTubeVideoId from "components/utils/getYoutubeVideoId";

type Content = NonNullable<IFundamenteiBlogPostFieldsFragment["body"]>;

function options(links: Content["links"]) {
  const findAsset = (id: string) => links?.assets.block.find((item) => item?.sys.id === id);
  const findBlockEntry = (id: string) => links?.entries.block.find((item) => item?.sys.id === id);

  return {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong className="font-bold">{text}</strong>,

      [MARKS.ITALIC]: (text) => <em className="italic">{text}</em>,

      [MARKS.UNDERLINE]: (text) => <u className="underline">{text}</u>,

      [MARKS.CODE]: (text) => <code className="rounded bg-gray-100 p-1 dark:bg-gray-800">{text}</code>,

      [MARKS.STRIKETHROUGH]: (text) => <del className="line-through">{text}</del>,

      [MARKS.SUPERSCRIPT]: (text) => <sup className="text-sm">{text}</sup>,

      [MARKS.SUBSCRIPT]: (text) => <sub className="text-sm">{text}</sub>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <p className="align-center text-base text-gray-700 dark:text-slate-300 md:text-lg">{children}</p>
      ),

      [BLOCKS.HEADING_1]: (_, children) => (
        <h1 className="text-2xl font-bold dark:text-gray-50 sm:text-3xl">{children}</h1>
      ),

      [BLOCKS.HEADING_2]: (_, children) => (
        <h2 className="text-xl font-bold dark:text-gray-50 sm:text-2xl">{children}</h2>
      ),

      [BLOCKS.HEADING_3]: (_, children) => (
        <h3 className="text-lg font-semibold dark:text-gray-100 sm:text-xl">{children}</h3>
      ),

      [BLOCKS.HEADING_4]: (_, children) => (
        <h4 className="text-base font-semibold dark:text-gray-100 sm:text-lg">{children}</h4>
      ),

      [BLOCKS.HEADING_5]: (_, children) => <h5 className="text-base font-medium dark:text-gray-100">{children}</h5>,

      [BLOCKS.HEADING_6]: (_, children) => (
        <h6 className="text-sm font-medium dark:text-gray-200 sm:text-base">{children}</h6>
      ),

      [BLOCKS.HR]: () => <hr className="border-t dark:border-t-gray-700" />,

      [BLOCKS.UL_LIST]: (_: unknown, children: React.ReactNode) => (
        <ul className="mb-4 flex list-disc flex-col gap-4 pl-6">{children}</ul>
      ),

      [BLOCKS.OL_LIST]: (_: unknown, children: React.ReactNode) => (
        <ol className="mb-4 flex list-decimal flex-col gap-4 pl-8">{children}</ol>
      ),

      [BLOCKS.LIST_ITEM]: (_: unknown, children: React.ReactNode) => <li>{children}</li>,

      [BLOCKS.QUOTE]: (_: unknown, children: React.ReactNode) => (
        <blockquote className="mb-4 rounded-r-lg border-l-4 border-zinc-200 px-4">{children}</blockquote>
      ),

      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const asset = findAsset(node?.data.target.sys.id);
        if (!asset || !asset.url) {
          return null;
        }
        return (
          <figure className="mb-6 flex flex-col items-center gap-2 rounded-xl">
            <img
              alt={asset.description || asset.title || ""}
              loading="lazy"
              width={asset.width || 300}
              height={asset.height || 400}
              src={asset.url}
            />
            {asset.description && (
              <figcaption className="break-all text-center text-xs font-medium text-zinc-500">
                {asset.description}
              </figcaption>
            )}
          </figure>
        );
      },

      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const entry = findBlockEntry(node?.data.target.sys.id);
        if (!entry) {
          return null;
        }
        if (entry.__typename === "YoutubeEmbed" && entry.url) {
          const videoId = getYouTubeVideoId(entry.url);
          if (!videoId) {
            return null;
          }
          return (
            <div className="relative aspect-video w-full">
              <iframe
                title="YouTube Embed"
                className="absolute left-0 top-0 h-full w-full rounded-lg"
                allowFullScreen={true}
                src={`https://www.youtube.com/embed/${videoId}`}
              />
            </div>
          );
        }
        return null;
      },

      [INLINES.HYPERLINK]: ({ data, nodeType }, children) => {
        if (nodeType === INLINES.HYPERLINK && data.uri) {
          return (
            <a
              href={data.uri}
              className="font-medium text-blue-500 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          );
        }
        return <span>{children}</span>;
      },
    },
  } satisfies Options;
}

interface ContentfulRichTextProps {
  content: Content | null;
}

export default function ContentfulRichText({ content }: ContentfulRichTextProps) {
  if (!content) {
    return null;
  }
  return <>{documentToReactComponents(content?.json, options(content.links))}</>;
}
